import { Modal } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { colors } from '../theme';

/**
 * Centralized notification helpers for consistent UX across the app.
 *
 * Usage:
 *   import { confirmDelete, confirmAction } from '../utils/notifications';
 *
 *   confirmDelete({
 *     title: 'Delete project',
 *     content: 'This will permanently remove the project.',
 *     onOk: async () => { await API.deleteProject(...); },
 *   });
 */

/** Destructive action confirmation (red) */
export const confirmDelete = ({ title, content, onOk, okText = 'Delete', ...rest }) =>
  Modal.confirm({
    title: title || 'Confirm deletion',
    icon: <DeleteOutlined style={{ color: colors.error }} />,
    content,
    okText,
    okType: 'danger',
    cancelText: 'Cancel',
    centered: true,
    onOk,
    ...rest,
  });

/** Warning-level confirmation (orange) */
export const confirmWarning = ({ title, content, onOk, okText = 'Proceed', ...rest }) =>
  Modal.confirm({
    title: title || 'Are you sure?',
    icon: <WarningOutlined style={{ color: colors.warning }} />,
    content,
    okText,
    okType: 'primary',
    cancelText: 'Cancel',
    centered: true,
    onOk,
    ...rest,
  });

/** Informational confirmation (blue) */
export const confirmAction = ({ title, content, onOk, okText = 'Confirm', ...rest }) =>
  Modal.confirm({
    title: title || 'Confirm action',
    icon: <InfoCircleOutlined style={{ color: colors.processing }} />,
    content,
    okText,
    cancelText: 'Cancel',
    centered: true,
    onOk,
    ...rest,
  });

/** Critical action confirmation with extra emphasis */
export const confirmCritical = ({ title, content, onOk, okText = 'I understand, proceed', ...rest }) =>
  Modal.confirm({
    title: title || 'Critical action',
    icon: <ExclamationCircleOutlined style={{ color: colors.error }} />,
    content: (
      <div>
        {typeof content === 'string' ? <p>{content}</p> : content}
        <p style={{ color: colors.error, fontWeight: 500, margin: '8px 0 0 0' }}>
          This action cannot be undone.
        </p>
      </div>
    ),
    okText,
    okType: 'danger',
    cancelText: 'Cancel',
    centered: true,
    onOk,
    ...rest,
  });
